/**
 * Work a page must send BEFORE the profile switch swaps the session.
 *
 * A switch (`useSwitchProfile` / `useSwitchToParent`) asks the server for a new
 * session, puts its token in the auth store, and reloads. A page that records
 * on its way out (the reader's reading time, on `pagehide` or unmount) used to
 * send that record AFTER the swap — under the next profile's token, which the
 * server refuses (the reading session belongs to the child who read). A parent
 * who switched mid-book lost the child's reading time and its XP.
 *
 * So the switch asks first: every page with exit work registers a flusher, and
 * the switch awaits all of them — capped, so a hung request can never hold a
 * parent inside the child's view. The page's own latches still make sure the
 * later `pagehide` does not send the same thing twice.
 */

type Flusher = () => Promise<unknown> | unknown;

const flushers = new Set<Flusher>();

/** How long a switch waits for exit work before it goes anyway. */
export const PROFILE_SWITCH_FLUSH_CAP_MS = 1500;

/** Register exit work; returns the unregister. */
export function onBeforeProfileSwitch(flush: Flusher): () => void {
  flushers.add(flush);
  return () => {
    flushers.delete(flush);
  };
}

/**
 * Run every registered flusher and wait for them, or for the cap, whichever is
 * first. Never throws: a failed flush must not stop the switch.
 */
export async function flushBeforeProfileSwitch(
  capMs: number = PROFILE_SWITCH_FLUSH_CAP_MS
): Promise<void> {
  if (flushers.size === 0) return;
  const work = [...flushers].map((flush) =>
    Promise.resolve()
      .then(flush)
      .catch(() => undefined)
  );
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, capMs);
  });
  await Promise.race([Promise.allSettled(work), cap]);
  if (timer) clearTimeout(timer);
}
